import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import Login from './components/Login';
import Topbar from './components/Topbar';
import Sidebar, { BottomNav } from './components/Sidebar';
import FleetPage from './pages/FleetPage';
import TagoutPage from './pages/TagoutPage';
import SparePage from './pages/SparePage';
import ServiceLogPage from './pages/ServiceLogPage';
import ReceiptsPage from './pages/ReceiptsPage';
import { RequestsPage, InspectionsPage, ImportPage, ReportsPage, ModelsPage } from './pages/OtherPages';
import { UsersPage } from './pages/UserManagementPage';
import { InventoryStockHome, InventoryCatalogPage, InventoryItemDetail, InventoryActionCatalog, InventoryLedgerPage, PurchasingQueuePage, VendorCatalogPage, RequestsInboxPage, MyRequestsPage, RequestMaterialsPage, OffCatalogReviewPage, InvoicingPage, TransfersPage, WeldingPage } from './pages/inventory';
import InventoryScanSheet from './pages/inventory/InventoryScanSheet';
import { itemOutstandingQty } from './lib/invoicing';
import InventoryLabelPrint from './pages/inventory/InventoryLabelPrint';
import ToolDetailPage from './pages/ToolDetailPage';
import SetPasswordPage from './pages/SetPasswordPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import { PrinterRegistryPage } from './pages/PrinterRegistryPage';
import InsightsPage from './pages/InsightsPage';
import NotificationsPage from './pages/NotificationsPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import { SpeakUpPublic, SpeakUpBoard, SpeakUpThread, SpeakUpAdminInbox, SpeakUpManager, SpeakUpLedger } from './pages/speakup';
import { isSpeakUpHost } from './lib/speakupHost';
import { supabase } from './lib/supabase';
import { pageFallbackFor, isKnownPage, pageLabel, landingPageFor, moduleForPage, equivalentPageFor, parseNamespacedLinkId, pageRuleAuth, pageRulePending } from './lib/deepLink';
import { isAdminManagerOrExecutive, isBranchScopedManager } from './lib/permissions';
import { MOBILE_BREAKPOINT } from './lib/layout';
import MyProfilePage from './pages/MyProfilePage';

function AppInner() {
  const auth = useAuth();
  const { user, profile, loading, signOut, profileState, retryProfile, isAdmin, isManager, isExecutive } = auth;
  // #746, Session 79.22b.6.3 -- what the page rule reads, built in ONE place
  // (deepLink.js, pageRuleAuth()) for all five of this file's rule calls, the
  // menu's and My profile's. Memoised on the context value, which changes only
  // when sign-in's own state does, so the landing effect below runs when the
  // answer can change and not on every render.
  const ruleAuth = useMemo(() => pageRuleAuth(auth), [auth]);
  // #167, Session 78j. Was useState('fleet').
  //
  // Starts null rather than on a page, and that is the whole point. The landing
  // page cannot be known until the profile and the role defaults have both
  // loaded, and #167's queue row is explicit that a build which changes only the
  // reset still paints Fleet Inventory first and then jumps -- "that flash reads
  // as a bug." null means "not resolved yet", the shell holds its existing
  // loading state one beat longer, and the first page anyone sees is the right
  // one.
  const [active, setActiveRaw] = useState(null);
  // null = not fetched yet, {} = fetched and empty. The landing effect waits for
  // the difference, so a slow settings fetch cannot land someone on 'fleet' and
  // then move them.
  const [landingDefaults, setLandingDefaults] = useState(null);
  const [isMobileShell, setIsMobileShell] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [activeModule, setActiveModule] = useState('tools');
  const [pendingCount, setPendingCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);
  const [offCatalogCount, setOffCatalogCount] = useState(0);
  const [invoicingCount, setInvoicingCount] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [openRequestId, setOpenRequestId] = useState(null);
  // #371, Session 79.6, gate D20. A THIRD id namespace, and it gets its own
  // state for the reason notificationNav.js spells out: a branch_transfers id
  // reaching setSelectedItemId would open an inventory item with the same
  // number, looking entirely normal.
  const [openTransferId, setOpenTransferId] = useState(null);
  // #689 D19, Session 79.22b.5.1 -- a Stock Home chip ("4 to make") opens
  // Transfers on THAT tab, looking at the person's own branch, which is what
  // the chip counted. Forgotten as soon as the page has read it, so the next
  // ordinary visit opens on the person's job as gate 665 D18 chooses.
  const [transfersEntry, setTransfersEntry] = useState(null);
  // #605, Session 79.21c. A FOURTH namespace, and it needs its own setter for
  // the same reason openTransferId did: the Requests inbox already reads its
  // link id as a material_requests id, so a branch_transfers id routed to
  // openRequestId would open the wrong record and look entirely normal.
  const [openTransferRequestId, setOpenTransferRequestId] = useState(null);
  const [scanItemId, setScanItemId] = useState(null);
  // #280, Session 78d. `scanItemId` carries no provenance — it means "an item was
  // preselected", not "an item was scanned", and the receive/use/adjust/request
  // header badge read it as if it did. Three of the four routes that set it are
  // not scans: a tapped low-stock card, a searched catalog hit, and the item
  // detail page's own New request button.
  //
  // This is the only place that knows which route fired, which is why the
  // provenance lives here rather than being inferred downstream.
  const [preloadSource, setPreloadSource] = useState(null);

  // Deliberately defaults to 'select' so the three child callers need no edit and
  // cannot forget: none of them is a scan, so the safe default is also the correct
  // answer for all of them. Only the ?scan_action= handler below passes 'scan'.
  // #684, Session 79.22b.4: Stock Home's purchase path passes 'buy' -- a purchase
  // chosen on purpose, which the request screen must not send back to a maker.
  const preselectItem = useCallback((id, source = 'select') => {
    setScanItemId(id);
    setPreloadSource(id == null ? null : source);
  }, []);
  const [batchRequestItems, setBatchRequestItems] = useState(null);
  const [filterItemId, setFilterItemId] = useState(null);
  const [filterItemName, setFilterItemName] = useState(null);
  const location = useLocation();

  // Issue #168. `active` and the id/filter state below it live in AppInner, which
  // never unmounts — signing out only swaps its return value for <Login />. So the
  // page key survives a sign-out while the page component itself is destroyed,
  // which is why signing back in as a different user reopens the previous user's
  // page. These two refs let the effect below tell a genuine new sign-in apart
  // from a token refresh, and from a QR deep link that has already chosen a page.
  const prevUserIdRef = useRef(null);
  const deepLinkHandledRef = useRef(false);
  // #167 — which profile id the landing has already been applied for. Without
  // this the effect would re-home someone every time the profile object was
  // re-fetched, yanking them off the page they had navigated to.
  const landingAppliedForRef = useRef(null);

  function setActive(page) {
    if (page !== 'purchasing_vendors') {
      setFilterItemId(null);
      setFilterItemName(null);
    }
    if (!['inv_receive', 'inv_use', 'inv_adjust', 'inv_request'].includes(page)) {
      // #280: clears the provenance with the id. A stale 'scan' outliving the
      // item it described is how the badge would start lying again.
      preselectItem(null);
    }
    if (page !== 'inv_request') {
      setBatchRequestItems(null);
    }
    setActiveRaw(page);
  }

  // #689 D19 -- see transfersEntry above.
  function openTransfersAt(tab, branchId) {
    setTransfersEntry({ tab, branchId: branchId ?? null });
    setActive('transfers');
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const scanAction = params.get('scan_action');
    const scanItem = params.get('scan_item');
    if (!scanAction) return;

    if (scanAction === 'detail' && scanItem) {
      setSelectedItemId(parseInt(scanItem, 10));
      setActiveRaw('inv_detail');
      setActiveModule('inventory');
    } else if (scanAction === 'use') {
      preselectItem(parseInt(scanItem, 10), 'scan');
      setActiveRaw('inv_use');
      setActiveModule('inventory');
    } else if (scanAction === 'receive') {
      preselectItem(parseInt(scanItem, 10), 'scan');
      setActiveRaw('inv_receive');
      setActiveModule('inventory');
    } else if (scanAction === 'adjust') {
      preselectItem(parseInt(scanItem, 10), 'scan');
      setActiveRaw('inv_adjust');
      setActiveModule('inventory');
    } else if (scanAction === 'request') {
      preselectItem(parseInt(scanItem, 10), 'scan');
      setActiveRaw('inv_request');
      setActiveModule('inventory');
    }

    deepLinkHandledRef.current = true;
    window.history.replaceState({}, '', '/');
  }, [location.search]);

  // #126, 78h. The other half of the /go/ route. DeepLinkResolver below decides
  // WHERE, and hands the page key over in history state rather than a query
  // string — the destination has already survived the login round-trip in the
  // path by this point, and putting it back into a query would re-expose it to
  // the same `from.pathname` truncation the gate's Finding 2 is about.
  //
  // Declared above the #168 reset effect deliberately: effects run in
  // declaration order, so this one claims deepLinkHandledRef before that effect
  // reads it. Without that, arriving from an email would set the page and then
  // be reset to Fleet on the same mount — which is the exact failure #168's own
  // comment records for the QR path.
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    const link = location.state?.deepLink;
    if (!link || deepLinkConsumedRef.current) return;
    deepLinkConsumedRef.current = true;

    const { linkTo, linkId } = link;
    if (linkId != null) {
      // The same split notificationNav.js makes, and for the same reason: a
      // request id and an inventory_items id live in different namespaces, so
      // they must never reach the same setter.
      // #391 D11, Session 78n — my_requests joins this branch. Both page keys
      // address a material_requests row, and the split below exists precisely
      // because a request id and an inventory_items id live in different
      // namespaces and must never reach the same setter.
      // #605, Session 79.21c. THE NAMESPACE IS CHECKED BEFORE THE PAGE KEY,
      // because a `transfer:` id is a branch_transfers row wherever it points
      // and parseInt() would turn it into NaN on any branch below.
      //
      // This branch exists because the comment below is right: a transfer
      // arriving from an EMAIL comes through here rather than through the bell,
      // and the ask-stage mail now carries a namespaced id too. Fixing only
      // notificationNav.js would have fixed the tap and not the link.
      const parsedLink = parseNamespacedLinkId(linkId);
      if (parsedLink && parsedLink.kind === 'transfer') setOpenTransferRequestId(parsedLink.id);
      else if (!parsedLink) { /* nothing usable — open the page without a record */ }
      else if (linkTo === 'requests_inbox' || linkTo === 'my_requests') setOpenRequestId(parsedLink.id);
      // #371 D20, Session 79.6. The third namespace. This branch and
      // notificationNav.js's ITEM_ID_DESTINATIONS are two copies of one rule and
      // must be changed together -- the comment above already says so, and a
      // transfer arriving from an EMAIL comes through here rather than through
      // the bell, so leaving this half out would fix the tap and not the link.
      else if (linkTo === 'transfers') setOpenTransferId(parsedLink.id);
      else setSelectedItemId(parsedLink.id);
    }
    setActiveRaw(linkTo);
    setActiveModule(moduleForPage(linkTo));
    deepLinkHandledRef.current = true;

    // Drop the state but keep the URL. Without this a browser back into this
    // entry would re-apply a destination the reader has already moved on from.
    window.history.replaceState({}, '');
  }, [location.state]);

  // Issue #168. A new sign-in lands on Fleet rather than reopening whatever page
  // the previous session left behind, and clears the navigation ids alongside it —
  // selectedItemId and openRequestId survive a sign-out the same way `active` does.
  // Keyed on user.id, not user, so a token refresh (which hands back a new object
  // for the same identity) does not reset a page mid-use. The deep-link guard is
  // required rather than defensive: the effect above runs on mount even while
  // <Login /> is showing, so a scanned QR code chooses its page before sign-in and
  // this effect would otherwise overwrite it. It cannot test location.search for
  // that, because the effect above has already cleared the query string.
  useEffect(() => {
    const currentId = user?.id ?? null;
    const previousId = prevUserIdRef.current;
    prevUserIdRef.current = currentId;

    if (!currentId) {
      // Only a genuine sign-out clears the deep-link guard. On first mount this
      // effect runs before auth has resolved, with currentId still null — clearing
      // unconditionally here wiped the flag the deep-link effect had just set on
      // the same mount, so the reset below then overwrote the scanned page. Found
      // by live test, not by reading.
      if (previousId) deepLinkHandledRef.current = false;
      return;
    }
    if (currentId === previousId) return;
    if (deepLinkHandledRef.current) {
      deepLinkHandledRef.current = false;
      return;
    }

    // #167 — was setActiveRaw('fleet') + setActiveModule('tools'). Now clears to
    // unresolved and lets the landing effect below decide, so a user switch
    // lands the new person where THEY belong rather than on Fleet Inventory.
    setActiveRaw(null);
    landingAppliedForRef.current = null;
    setSelectedItemId(null);
    setOpenRequestId(null);
    setOpenTransferId(null);
    preselectItem(null);
    setBatchRequestItems(null);
    setFilterItemId(null);
    setFilterItemName(null);
    setShowSidebar(false);
  }, [user?.id]);

  // Issue #243. Deliberately not branch-scoped — catalog entries are company-wide
  // master data and purchasing already spans branches. The status filter is one hop
  // through an inner join, which PostgREST evaluates; two or more hops return empty
  // silently. Verified at 74n against the page's own header count.
  //
  // Exposed to OffCatalogReviewPage rather than left inside the effect below,
  // because that effect only re-runs when `active` changes. Dismissing the last
  // waiting row left the sidebar badge reading 1 against a page reading 0 until the
  // next navigation — the same shape as #255, a surface reporting a number that
  // does not describe what it claims. Caught by live test at 74n.
  const refreshOffCatalogCount = useCallback(() => {
    supabase
      .from('material_request_items')
      .select('id, material_requests!inner(status)', { count: 'exact' })
      .eq('is_off_catalog', true)
      .is('item_id', null)
      .is('catalog_review_dismissed_at', null)
      .in('material_requests.status', ['ordered', 'received', 'closed'])
      .then(({ count }) => setOffCatalogCount(count || 0));
  }, []);

  // #284, Session 74z. The Invoicing entry's badge. It cannot be a PostgREST
  // `count: 'exact'` like the one above, because "invoiceable" is arithmetic —
  // received quantity minus what has already been billed across every invoice
  // line — and PostgREST cannot subtract. So the narrow shape the arithmetic
  // needs is fetched and counted here, using the same predicate the page itself
  // uses rather than an approximation of it.
  //
  // Approximating would have been the cheap option and it is the wrong one: a
  // badge that disagrees with the page it points at is exactly the defect #255
  // and the off-catalog count above were each opened for.
  const refreshInvoicingCount = useCallback(() => {
    supabase
      .from('material_request_items')
      .select('id, qty_received, material_requests!inner(id, status), material_request_invoice_lines ( qty_invoiced )')
      .gt('qty_received', 0)
      .in('material_requests.status', ['ordered', 'received'])
      .then(({ data }) => {
        const openRequestIds = new Set(
          (data || [])
            .filter(it => itemOutstandingQty(it) > 0.0001)
            .map(it => it.material_requests?.id)
            .filter(id => id != null)
        );
        setInvoicingCount(openRequestIds.size);
      });
  }, []);

  useEffect(() => {
    if (!user) return;
    supabase.from('spare_requests').select('id', { count: 'exact' }).eq('status', 'pending').then(({ count }) => setPendingCount(count || 0));
    // #358, Session 78l. Two of that issue's three defects are fixed here, at
    // the fetch rather than at the three places the number renders.
    //
    // Shown to people who cannot act on it: the count is material requests
    // awaiting approval, and canApprove.js admits admin, executive and a
    // branch-matched manager -- nobody else. A team_member was getting a red
    // badge on the bottom bar, in the collapsed rail's flyout AND in the
    // expanded accordion. Gating the fetch fixes all three from one place and
    // saves the query; gating each render site would have been three chances
    // to miss one. The third defect -- the badge sitting on the Stock slot,
    // which does not open the queue it counts -- is fixed in Sidebar.js.
    //
    // The scope now MIRRORS RequestsInboxPage's own load() exactly, and that
    // is the point rather than a tidy-up. The old code branch-scoped anyone
    // holding a branch_id, which includes an admin; the page they open does
    // not, because it scopes on isBranchScopedManager. So Colby's badge
    // counted Dallas while the list it led to showed every branch. A badge
    // whose number disagrees with the list underneath it is the same defect
    // #358 is about, one role over -- found while fixing it, fixed with it.
    if (isAdminManagerOrExecutive({ isAdmin, isManager, isExecutive })) {
      let inboxQuery = supabase
        .from('material_requests')
        .select('id', { count: 'exact' })
        .eq('status', 'pending');
      if (isBranchScopedManager({ isAdmin, isManager, isExecutive })) {
        inboxQuery = inboxQuery.eq('branch_id', profile.branch_id);
      }
      inboxQuery.then(({ count }) => setInboxCount(count || 0));
    } else {
      setInboxCount(0);
    }
    refreshOffCatalogCount();
    refreshInvoicingCount();
  }, [user, active, refreshOffCatalogCount, refreshInvoicingCount]);

  // #167, D2 — the role default is data, not code, so that changing where
  // managers land is a settings change rather than a deploy. Read once per
  // session; company_settings is a single row.
  useEffect(() => {
    let cancelled = false;
    if (!user) return undefined;
    supabase
      .from('company_settings')
      .select('landing_defaults')
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        // {} rather than null on failure or on an empty column: null means "still
        // loading" to the effect below, and a failed fetch that stayed null would
        // hold every user on the loading spinner forever. An empty map simply
        // means every role falls through to 'fleet', which is today's behaviour.
        if (!cancelled) setLandingDefaults(data?.landing_defaults || {});
      });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    function handleResize() { setIsMobileShell(window.innerWidth < MOBILE_BREAKPOINT); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // #167 — resolve the landing page once both halves are in.
  //
  // Deliberately does NOT run when a deep link has already claimed this mount.
  // Someone arriving from a notification or a QR code has a destination that
  // beats their preference, and overwriting it here would undo #126's whole
  // route as well as the ?scan_action= path.
  //
  // D3's platform split is read from the viewport at the moment of landing,
  // which is the only honest reading of "on your phone" for a responsive app
  // that is installed as a PWA on both.
  useEffect(() => {
    if (!profile?.id || landingDefaults === null) return;
    if (deepLinkHandledRef.current) return;
    if (landingAppliedForRef.current === profile.id) return;
    if (active !== null) { landingAppliedForRef.current = profile.id; return; }

    // #746 -- a Welding opening page is chosen even while the sign-in has not
    // heard which branches weld; the page shows itself loading meanwhile, and
    // the rule applies when the answer lands (deepLink.js, landingPageFor()).
    const target = landingPageFor(
      ruleAuth,
      { profile, roleDefaults: landingDefaults, isMobile: isMobileShell },
    );
    landingAppliedForRef.current = profile.id;
    setActiveRaw(target);
    setActiveModule(moduleForPage(target));
  }, [profile, landingDefaults, active, isMobileShell, ruleAuth]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' }}>
      <div className="flex-center gap-1"><div className="spinner" /><span style={{ color: 'var(--gray-lt)' }}>Loading Stede...</span></div>
    </div>
  );

  if (!user) return <Login />;

  // #167 — the anti-flash guard. `active` is null only between sign-in and the
  // landing effect resolving, which is one render in practice. Reusing the
  // existing spinner rather than adding a second loading state: a user who sees
  // two different loading screens in a row reads it as the app restarting.
  if (profile && active === null) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' }}>
      <div className="flex-center gap-1"><div className="spinner" /><span style={{ color: 'var(--gray-lt)' }}>Loading Stede...</span></div>
    </div>
  );

  // #268 — a session alone is not authorization. AuthContext signs out any
  // session with no profile row or a deactivated one, but this gate fails
  // closed independently rather than trusting that sign-out to have landed.
  if (profileState === 'error') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 320 }}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: 8 }}>Couldn't load your account</div>
        <div style={{ fontSize: '13px', color: 'var(--gray-lt)', marginBottom: 20 }}>
          Check your connection and try again.
        </div>
        <button className="login-btn" style={{ maxWidth: 200 }} onClick={retryProfile}>Try again</button>
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={signOut}
            style={{ background: 'none', border: 'none', color: 'var(--gray-lt)', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );

  if (!profile) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' }}>
      <div className="flex-center gap-1"><div className="spinner" /><span style={{ color: 'var(--gray-lt)' }}>Loading Stede...</span></div>
    </div>
  );

  const pages = {
    fleet:      <FleetPage />,
    tagout:     <TagoutPage />,
    spare:      <SparePage />,
    browse:     <SparePage isBrowse />,
    requests:   <RequestsPage />,
    inspect:    <InspectionsPage />,
    receipts:   <ReceiptsPage />,
    users:      <UsersPage />,
    import:     <ImportPage />,
    reports:    <ReportsPage />,
    models:     <ModelsPage />,
    servicelog: <ServiceLogPage />,
    inv_home:    <InventoryStockHome setActive={setActive} setScanItemId={preselectItem} setBatchRequestItems={setBatchRequestItems} openTransfersAt={openTransfersAt} />,
    inv_catalog: <InventoryCatalogPage setActive={setActive} setSelectedItemId={setSelectedItemId} />,
    inv_detail:  <InventoryItemDetail
                   itemId={selectedItemId}
                   setActive={setActive}
                   setFilterItemId={setFilterItemId}
                   setFilterItemName={setFilterItemName}
                   setOpenRequestId={setOpenRequestId}
                   setScanItemId={preselectItem}
                 />,
    inv_receive: <InventoryActionCatalog mode="receive" setActive={setActive} preloadItemId={scanItemId} preloadSource={preloadSource} />,
    inv_use:     <InventoryActionCatalog mode="use" setActive={setActive} preloadItemId={scanItemId} preloadSource={preloadSource} />,
    inv_adjust:  <InventoryActionCatalog mode="adjust" setActive={setActive} preloadItemId={scanItemId} preloadSource={preloadSource} />,
    inv_request: <InventoryActionCatalog mode="request" setActive={setActive} preloadItemId={scanItemId} preloadSource={preloadSource} preloadCartItems={batchRequestItems} />,
    inv_ledger:  <InventoryLedgerPage setActive={setActive} />,
    inv_labels:  <InventoryLabelPrint setActive={setActive} />,
    requests_inbox:     <RequestsInboxPage setActive={setActive} openRequestId={openRequestId} setOpenRequestId={setOpenRequestId} openTransferRequestId={openTransferRequestId} setOpenTransferRequestId={setOpenTransferRequestId} />,
    // #391, Session 78n. Takes openRequestId for the same reason the inbox does
    // -- D11 points the approved and denied notifications here, and they carry
    // a request id. Deliberately NOT given setActive: the page is read-only and
    // navigates nowhere, which is D4 expressed as a prop list.
    my_requests:        <MyRequestsPage openRequestId={openRequestId} setOpenRequestId={setOpenRequestId} />,
    // #371 and #425, Session 79.6. Takes openTransferId for the same reason
    // my_requests takes openRequestId -- a transfer notification names a
    // branch_transfers row, which is its own id namespace (gate D20).
    // #621, Session 79.22b -- and setActive with setOpenRequestId, so a refusal
    // routed to Purchasing can open the purchase request it raised (gate 565's
    // "a link to the successor"), which lives in the Requests inbox.
    transfers:          <TransfersPage openTransferId={openTransferId} setOpenTransferId={setOpenTransferId} setActive={setActive} setOpenRequestId={setOpenRequestId}
                          initialTab={transfersEntry?.tab} initialBranchView={transfersEntry?.branchId ?? null} onEntryUsed={() => setTransfersEntry(null)} />,
    // #515 D33, Session 79.8. The To make queue is the SAME page opened on its
    // own tab, not a second screen: it is a filtered view of transfers plus
    // this branch's own reserve work, and a second reader of one query is how
    // the in-transit figure went wrong (#512).
    //
    // It needs its own key so a fabrication manager can be given it as a
    // landing page. The key already existed for deep links and pointed at
    // nothing — a notification could name it and the page would not render.
    production_queue:   <TransfersPage openTransferId={openTransferId} setOpenTransferId={setOpenTransferId} setActive={setActive} setOpenRequestId={setOpenRequestId} initialTab="to_make" />,
    // #746, Session 79.22b.6.3. Houston's welders' board. A key missing here
    // silently renders Fleet, which is why weldingPageKey746.test.js holds all
    // eleven places the gate names to it.
    welding:            <WeldingPage />,
    inv_request_materials: <RequestMaterialsPage setActive={setActive} setScanItemId={preselectItem} setBatchRequestItems={setBatchRequestItems} />,
    inv_offcatalog:     <OffCatalogReviewPage onCountsChanged={refreshOffCatalogCount} />,
    purchasing_queue:   <PurchasingQueuePage setActive={setActive} />,
    // #295, 74z2: setActive dropped — Invoicing no longer navigates anywhere.
    // "View full order" became an expandable section on the detail itself.
    purchasing_invoicing: <InvoicingPage onCountsChanged={refreshInvoicingCount} />,
    purchasing_vendors: <VendorCatalogPage
                          setActive={setActive}
                          setSelectedItemId={setSelectedItemId}
                          filterItemId={filterItemId}
                          filterItemName={filterItemName}
                        />,
    // #308, 74z6. Shell-level rather than inside a module accordion — this
    // surface spans purchasing, inventory and (after Sep 19) tools, so nesting
    // it under one of them would be wrong the moment a second module's KPI
    // lands. See the mockup gate, decision D2.
    insights: <InsightsPage setActive={setActive} />,
    company_settings: <CompanySettingsPage />,
    // #298, 78n9.3. Its own destination rather than a section of company
    // settings — an integration is a live connection with state, not a value
    // you pick once. Overturns gate decision D14 on Colby's instruction.
    integrations: <IntegrationsPage />,
    printer_registry: <PrinterRegistryPage />,
    speak_up_admin: <SpeakUpAdminInbox />,
    // Speak Up OOB-4. Three separate surfaces on purpose, because the gate
    // gives each one different reading rules: the handler sees submissions,
    // a manager sees their branch's ideas plus summaries shared with them,
    // and an executive sees counts with no way to open anything.
    speak_up_manager: <SpeakUpManager />,
    speak_up_ledger: <SpeakUpLedger />,
    notifications: <NotificationsPage setActive={setActive} setSelectedItemId={setSelectedItemId} setOpenRequestId={setOpenRequestId} setOpenTransferId={setOpenTransferId} setOpenTransferRequestId={setOpenTransferRequestId} />,
    notification_preferences: <NotificationPreferencesPage setActive={setActive} />,
    profile: <MyProfilePage landingDefaults={landingDefaults} />,
  };

  // #126, 78h. The four role rules that used to be written out here now live in
  // src/lib/deepLink.js, because the /go/ resolver needs the same answer and a
  // second copy of them would drift. Behaviour here is unchanged — the fallback
  // page keys are the same ones this function returned components for — and the
  // purchasing test now reads the shared isStaffWithPurchasing predicate rather
  // than negating four role flags inline, per locked rule 8.
  function getPage(id) {
    const fallback = pageFallbackFor(ruleAuth, id);
    // #746, Session 79.22b.6.3 -- a page whose answer is still on its way renders
    // ITSELF, and it shows its own loading line; another page is never put in
    // its place while the sign-in has not heard (746 D5, gate F4). The rule
    // applies the moment it hears.
    if (fallback && !pageRulePending(ruleAuth, id)) return pages[fallback];
    return pages[id] || <FleetPage />;
  }

  // #319, 74z8 — every table header in the app was Inventory green, including
  // Purchasing's and Invoicing's, whose own icons and mobile card rails are
  // already amber and magenta. Colby spotted it from a screenshot and expected
  // amber, which is the tell that it reads as wrong to a user and not only to
  // a stylesheet.
  //
  // Resolved here rather than in each page because `thead th` is a single
  // global rule with no knowledge of which module it is inside. This sets one
  // custom property on the page container; index.css's `thead th` reads it and
  // falls back to green, so a page not listed below is unchanged rather than
  // unstyled. A future module is one entry, not a stylesheet edit.
  //
  // Colours are the locked domain set — see CLAUDE.md, "Domain colors".
  function domainForPage(id) {
    if (id === 'purchasing_invoicing') return 'invoicing';
    if (['purchasing_queue', 'purchasing_vendors', 'inv_offcatalog'].includes(id)) return 'purchasing';
    // #746: Welding is an Inventory page - green table headers, as drawn.
    if (id.startsWith('inv_') || id === 'requests_inbox' || id === 'my_requests' || id === 'welding') return 'inventory';
    if (['fleet', 'tools', 'models', 'servicelog', 'spare', 'printer_registry'].includes(id)) return 'tools';
    if (id.startsWith('speak_up')) return 'safety';
    // Shell-level surfaces — Insights, settings, notifications, profile — are
    // deliberately not domain-tinted. Insights spans purchasing, inventory and
    // (after Sep 19) tools, so claiming one module's accent would be a lie
    // about what the page covers. They fall back to green.
    return undefined;
  }

  return (
    <div className="app-shell">
      <Topbar
        user={user}
        profile={profile}
        active={active}
        signOut={signOut}
        isAdmin={isAdmin}
        isManager={isManager}
        isExecutive={isExecutive}
        pendingCount={pendingCount}
        setActive={setActive}
        setSelectedItemId={setSelectedItemId}
        setOpenRequestId={setOpenRequestId}
        setOpenTransferId={setOpenTransferId}
        setOpenTransferRequestId={setOpenTransferRequestId}
        onMenuToggle={() => setShowSidebar(v => !v)}
        // #167 — the third of the three 'fleet' literals. The logo means "take
        // me home", and home is the person's landing page rather than the Tools
        // module's front page.
        onLogoClick={() => {
          const home = landingPageFor(
            ruleAuth,
            { profile, roleDefaults: landingDefaults, isMobile: isMobileShell },
          );
          setActive(home);
          setActiveModule(moduleForPage(home));
          setShowSidebar(false);
        }}
      />

      <div className="main-layout">
        <Sidebar
          active={active}
          setActive={setActive}
          pendingCount={pendingCount}
          inboxCount={inboxCount}
          offCatalogCount={offCatalogCount}
          invoicingCount={invoicingCount}
          isOpen={showSidebar}
          onClose={() => setShowSidebar(false)}
          activeModule={activeModule}
          setActiveModule={setActiveModule}
        />

        <main className="main-content" data-domain={domainForPage(active)}>
          {getPage(active)}
        </main>
      </div>

      <BottomNav
        active={active}
        setActive={setActive}
        pendingCount={pendingCount}
        inboxCount={inboxCount}
        activeModule={activeModule}
        setActiveModule={setActiveModule}
      />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, profile, loading, profileState } = useAuth();
  const location = useLocation();
  // #268 — the profile check has to gate this route guard too. Without it a
  // deep link stayed reachable on a session the main gate would have refused,
  // which is the whole point of having a second entry path.
  if (loading || (user && !profile && profileState !== 'error')) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' }}>
      <div className="flex-center gap-1"><div className="spinner" /><span style={{ color: 'var(--gray-lt)' }}>Loading...</span></div>
    </div>
  );
  if (!user || !profile) return <Navigate to="/" state={{ from: location }} replace />;
  return children;
}

// #373, Session 78g. A Supabase recovery link establishes a real session, so
// without this the user simply lands in the app, signed in, and is never asked
// to set a password. The account then has no password at all, which means the
// only way back in on any future device is another emailed link from an admin.
//
// Sits ABOVE every route rather than inside RequireAuth, because the failure was
// precisely that the link did not land on the route that handles it. Enforcing
// this at the route level is what broke; enforcing it on the auth state is what
// cannot be routed around — a wrong redirect_to, a Site URL fallback, an
// unallowlisted origin and a PKCE ?code= callback all end up here identically.
function RecoveryGate({ children }) {
  const { passwordRecovery } = useAuth();
  const location = useLocation();
  if (passwordRecovery && location.pathname !== '/set-password') {
    return <Navigate to="/set-password" replace />;
  }
  return children;
}

// #126, Session 78h — gate decisions E3 and E7.
//
// E3: this renders nothing in the ordinary case. It resolves, redirects and
// disappears from history. `replace` rather than a push is not tidiness — if it
// pushed, the back button from the Requests inbox would land on a resolver that
// immediately resolves again, a loop the reader cannot escape by going back.
//
// E7: a destination the reader's role cannot reach lands them on their own
// landing page and NAMES what it could not open. Roles change and notifications
// outlive them; a 403 from an email is the worst possible outcome, and "you
// don't have access" with no subject is a dead end. Naming the page is what
// makes it reportable — they can tell an administrator exactly what they could
// not reach.
function DeepLinkResolver() {
  // #746, Session 79.22b.6.3 -- the rule's input from the one helper (deepLink.js,
  // pageRuleAuth()), like the shell's: this resolver runs before the shell
  // mounts, so it is a sixth and seventh place that could otherwise drift.
  const ruleAuth = pageRuleAuth(useAuth());
  const { linkTo, linkId } = useParams();
  const navigate = useNavigate();

  const known = isKnownPage(linkTo);

  // #746 -- a link to a page whose answer is still on its way WAITS: refusing it
  // now would tell a welder their account cannot open Welding at the moment the
  // sign-in is about to say it can (746 D5). This renders again when it hears.
  if (known && pageRulePending(ruleAuth, linkTo)) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' }}>
      <div className="flex-center gap-1"><div className="spinner" /><span style={{ color: 'var(--gray-lt)' }}>Loading...</span></div>
    </div>
  );

  const blocked = known ? pageFallbackFor(ruleAuth, linkTo) : true;

  if (known && !blocked) {
    return <Navigate to="/" replace state={{ deepLink: { linkTo, linkId: linkId ?? null } }} />;
  }

  // #391, Session 78n. E7 refuses rather than substituting, and that is correct
  // for every case but one. A team member whose link points at the approval
  // queue belongs on My requests -- the page showing the very request the
  // notification was about -- so this redirects, carrying linkId through so the
  // request is selected on arrival rather than merely being somewhere in a list.
  //
  // Load-bearing for the BACKLOG, not only for new mail: every approval and
  // denial notification sent before D11 retargeted the two call sites still
  // carries `requests_inbox`, and those stored rows do not rewrite themselves.
  // Without this each one is a dead end telling its reader they lack access to
  // something Stede sent them.
  const equivalent = known ? equivalentPageFor(ruleAuth, linkTo) : null;
  if (equivalent) {
    return <Navigate to="/" replace state={{ deepLink: { linkTo: equivalent, linkId: linkId ?? null } }} />;
  }

  // #391, Session 78n — raised by Colby from a live screenshot: this screen
  // offered a team member "Go to Fleet Inventory", which is not their home page.
  //
  // The DESTINATION was never wrong. The button calls navigate('/'), and the
  // shell then computes the landing page properly, with `roleDefaults` from
  // company_settings -- so a team member whose role default is Stock Home has
  // always arrived at Stock Home. Only the LABEL was wrong.
  //
  // It was wrong for a specific reason worth keeping: this resolver renders
  // before the shell mounts and deliberately does no settings fetch of its own,
  // so its landingPageFor() call could see a personal override or nothing at
  // all -- never the role default. It therefore printed 'fleet' for everybody
  // who had not personally chosen a page, which is most people.
  //
  // Fixed by not naming a page this component has not actually computed. The
  // previous code named one and was wrong; fetching company_settings here to
  // improve the wording of an error screen would cost every deep link a round
  // trip. Saying "your home page" is true for every role without either cost,
  // and stays true when Safety becomes an installer's default.
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 340 }}>
        <div style={{ fontSize: '17px', fontWeight: 700, marginBottom: 10 }}>Couldn't open that</div>
        <div style={{ fontSize: '13px', color: 'var(--gray-lt)', marginBottom: 6 }}>
          {known
            ? `This link points to ${pageLabel(linkTo)}.`
            : 'This link points to something Stede no longer has.'}
        </div>
        <div style={{ fontSize: '13px', color: 'var(--gray-lt)', marginBottom: 22, lineHeight: 1.5 }}>
          {known
            ? "Your account doesn't have access to it. If that's wrong, ask an administrator to check your role."
            : 'Nothing is wrong with your account. The page it was sent for has moved or been removed.'}
        </div>
        <button className="login-btn" style={{ maxWidth: 240 }} onClick={() => navigate('/', { replace: true })}>
          Go to your home page
        </button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <RecoveryGate>
      <Routes>
        <Route path="/tool/:id" element={
          <RequireAuth>
            <ToolDetailPage resourceType="tool" />
          </RequireAuth>
        } />
        <Route path="/consumable/:id" element={
          <RequireAuth>
            <ToolDetailPage resourceType="consumable" />
          </RequireAuth>
        } />
        <Route path="/inventory/scan/:itemId" element={
          <RequireAuth>
            <InventoryScanSheet />
          </RequireAuth>
        } />
        {/* #126, Session 78h — the address every notification email points at.
            Inside RequireAuth on purpose, and that is the load-bearing detail
            rather than a formality: a signed-out recipient is redirected with
            `from: location`, which carries the whole path, and Login sends them
            back here afterwards. Gate decision E4 — this grants no access of its
            own; it is not a magic link. */}
        <Route path="/go/:linkTo/:linkId?" element={
          <RequireAuth>
            <DeepLinkResolver />
          </RequireAuth>
        } />
        {/* #361, Session 78g — a literal /inventory route rendering "Inventory —
            Coming Soon" sat here, above the catch-all, for the module going live
            Sep 1. Nothing in src/ or functions/ ever generated that URL (labels
            use /inventory/scan/:id via labelTargetUrl), so it was reachable only
            by typing it — which is exactly what someone will do. Deleted rather
            than repointed: /inventory now falls through to the catch-all and
            lands in the app like any other unknown path. */}
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/speak-up" element={<SpeakUpPublic />} />
        <Route path="/speak-up/board" element={<SpeakUpBoard />} />
        <Route path="/speak-up/t/:token" element={<SpeakUpThread />} />
        {/* Speak Up OOB-5, gate decision D26. On Speak Up's OWN hostname the
            application does not exist: every other path lands on the front door
            rather than on a Stede login. Two reasons, and the second is the
            load-bearing one.

            D28 says Speak Up must not outwardly appear to be part of Stede, and
            a login screen one wrong tap away undoes that.

            AND A LOGIN SCREEN ON THAT HOST WOULD RE-CREATE #474. Signing in
            there would write a session into THAT origin's localStorage, and the
            next anonymous submission from the same browser would carry the
            reporter's own token again — measured at OOB-5 as carrying their
            EMAIL ADDRESS in the claims. The separation holds only while nobody
            can authenticate on this hostname, so nobody can. */}
        <Route path="/*" element={isSpeakUpHost() ? <Navigate to="/speak-up" replace /> : <AppInner />} />
      </Routes>
      </RecoveryGate>
    </AuthProvider>
  );
}